import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EProcessStatus, IProcessDto } from 'core/dto';
import { ScreenUtilsService } from 'core/services';
import { browserUtils } from 'core/utils';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, map, of, takeUntil } from 'rxjs';
import { designerPaths } from '../../designer.paths';
import { UserService } from '../../global-services/user.service';
import { DsgDeepTranslationKey } from '../../pipes/translate.pipe';
import { DataGridComponent } from '../../shared-components/data-grid/data-grid.component';
import { PageBaseDirective } from '../../shared-components/page-base.directive';
import { LProcessAction } from './process-action-template/process-actions.service';
import { ProcessOverviewService } from './process-overview.service';

const statusTranslations: Record<EProcessStatus, DsgDeepTranslationKey> = {
  [EProcessStatus.Cancelled]: null,
  [EProcessStatus.Draft]: 'ss-routes.drafts',
  [EProcessStatus.Expired]: 'ss-routes.expired',
  [EProcessStatus.Rejected]: 'ss-routes.rejected',
  [EProcessStatus.Finished]: 'ss-routes.finished',
  [EProcessStatus.WaitingForMe]: 'ss-routes.for-me-long',
  [EProcessStatus.WaitingForOthers]: 'ss-routes.for-others-long',
  [EProcessStatus.InProgress]: 'ss-routes.for-all-long',
  [EProcessStatus.Inactive]: '',
  [EProcessStatus.None]: null,

}

@Component({
  selector: 'ss-process-overview',
  templateUrl: './process-overview.component.html',
  styleUrls: ['./process-overview.component.scss'],
  host: {
    class: 'display-block full-height'
  },
  providers: [ProcessOverviewService],
  hostDirectives: [PageBaseDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProcessOverviewComponent {
  private readonly _destroy = new Subject<void>()

  readonly showSearchSubject = new BehaviorSubject<boolean>(false);
  readonly loadObs = this._processOverviewService.loadObs;
  readonly columnsSubject = this._processOverviewService.columnsSubject;
  readonly smallScreenObs = this._screenUtilsService.screenSizeSubject.pipe(
    map((size) => size === 'sm' || size === 'xs')
  )

  readonly titleObs = this._processOverviewService.statusSubject.pipe(
    map((status): DsgDeepTranslationKey => {
      if (!status) {
        return 'ss-routes.all'
      }

      return statusTranslations[status];
    })
  )

  @ViewChild('gridRef')
  private readonly _gridRef: DataGridComponent

  readonly getRowLoaderKeys = this._processOverviewService.getRowLoaderKeys;
  get isCreator() {
    return this._userService.isCreator
  }

  readonly isLargeScreenObs = combineLatest([this.smallScreenObs, of(browserUtils.isMobile())]).pipe(
    map(arr => !arr.some(Boolean)),
    distinctUntilChanged()
  )

  get isSmallScreen() {
    const screenSize = this._screenUtilsService.screenSizeSubject.value
    return screenSize === 'sm' || screenSize === 'xs'
  }

  readonly updateDataFn = this._processOverviewService.updateDataFn

  constructor(
    private readonly _processOverviewService: ProcessOverviewService,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _userService: UserService,
    private readonly _screenUtilsService: ScreenUtilsService,

  ) {

    this._activatedRoute.params.pipe(
      map((params) => params.status && Number(params.status)),
      takeUntil(this._destroy)
    )
      .subscribe(status => {
        this._processOverviewService.statusSubject.next(status);
      })





  }

  routeToDetail(rowData: IProcessDto) {

    if (rowData.status === EProcessStatus.Draft) {

      if (browserUtils.isMobile() || this.isSmallScreen) {
        return;
      }

      this._router.navigate([designerPaths.process, rowData.id], { queryParams: { processStatus: this._processOverviewService.status } });
      return;
    }



    this._router.navigate([designerPaths.processOverviewDetail, rowData.id], { queryParams: { processStatus: this._processOverviewService.status } })
  }

  routeToWizard() {
    this._router.navigate([designerPaths.process], { queryParams: { processStatus: this._processOverviewService.status } })
  }

  actionCompleted(processAction: LProcessAction) {

    if (processAction === 'download' || processAction === 'download-audit-trail') {
      return;
    }

    this._gridRef.refresh()
  }


  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }
}
